import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LoginForm } from "./LoginForm";
import { RegisterForm } from "./RegisterForm";
import { ForgotPasswordForm } from "./ForgotPasswordForm";
import { UserRole } from "@/lib/schema";
import tapLogo from "../../assets/tap-logo.png";

interface AuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type AuthMode = "login" | "register" | "forgot-password";

export function AuthModal({ open, onOpenChange }: AuthModalProps) {
  const [mode, setMode] = useState<AuthMode>("login");

  const handleClose = () => {
    setMode("login");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-gradient-to-br from-white via-slate-50/30 to-blue-50/20 border-0 shadow-2xl backdrop-blur-sm" style={{ paddingTop: 'calc(1.5rem + env(safe-area-inset-top, 0px))' }}>
        {/* Subtle background pattern */}
        <div className="absolute inset-0 rounded-lg opacity-5">
          <div className="absolute top-4 left-4 w-32 h-32 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-4 right-4 w-24 h-24 bg-gradient-to-br from-orange-400 to-pink-400 rounded-full blur-3xl"></div>
        </div>
        
        <DialogHeader className="text-center space-y-4 pb-2 relative z-10">
          <div className="inline-flex items-center justify-center w-16 h-16 mx-auto mb-2">
            <img 
              src={tapLogo} 
              alt="T.A.P. Logo" 
              className="w-full h-full object-contain"
            />
          </div>
          <DialogTitle className="text-2xl font-bold text-slate-800 space-grotesk">
            {mode === "login" && "Welcome Back"}
            {mode === "register" && "Join AirTalent"}
            {mode === "forgot-password" && "Reset Password"}
          </DialogTitle>
          <DialogDescription className="text-slate-600 space-grotesk font-medium">
            {mode === "login" && "Sign in to your account to continue"}
            {mode === "register" && "Create your account and start referring talent today"}
            {mode === "forgot-password" && "Enter your email to receive reset instructions"}
          </DialogDescription>
        </DialogHeader>

        <div className="relative z-10">
          {mode === "login" && (
            <div className="space-y-6">
              <LoginForm 
                onSuccess={handleClose} 
                onForgotPassword={() => setMode("forgot-password")}
              />
              <div className="text-center">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-gradient-to-br from-white via-slate-50/30 to-blue-50/20 px-3 text-slate-500 space-grotesk font-medium tracking-wider">NEW TO AIRTALENT?</span>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  onClick={() => setMode("register")}
                  className="mt-4 font-bold space-grotesk tracking-wide"
                >
                  Create your account
                </Button>
              </div>
            </div>
          )}

          {mode === "register" && (
            <div className="space-y-6">
              <RegisterForm role={UserRole.USER} onSuccess={handleClose} />
              <div className="text-center">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-gradient-to-br from-white via-slate-50/30 to-blue-50/20 px-3 text-slate-500 space-grotesk font-medium tracking-wider">ALREADY HAVE AN ACCOUNT?</span>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  onClick={() => setMode("login")}
                  className="mt-4 font-bold space-grotesk tracking-wide"
                >
                  Sign in instead
                </Button>
              </div>
            </div>
          )}

          {mode === "forgot-password" && (
            <ForgotPasswordForm onBackToLogin={() => setMode("login")} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
